/**
 * Admin Users management page (Doc 47).
 *
 * Features:
 * - List paginated users
 * - Toggle active/inactive
 * - Map / clear Odoo identity
 * - Force reset password
 * - Grant / revoke role
 */
import {
  CheckCircle2,
  Copy,
  KeyRound,
  Link2,
  Loader2,
  Mail,
  Plug,
  RefreshCw,
  Send,
  ShieldAlert,
  UserPlus,
  Users as UsersIcon,
  XCircle,
} from "lucide-react";
import * as React from "react";

import {
  type AdminUser,
  type InviteUserResponse,
  usersApi,
} from "@/api/users";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useUIStore } from "@/stores/ui";

function fmtDate(s: string | null): string {
  if (!s) return "—";
  return new Date(s).toLocaleString("vi-VN");
}

interface OdooMappingDialogProps {
  user: AdminUser;
  onClose: () => void;
  onSaved: (next: AdminUser) => void;
}

function OdooMappingDialog({
  user,
  onClose,
  onSaved,
}: OdooMappingDialogProps): JSX.Element {
  const pushToast = useUIStore((s) => s.pushToast);
  const [odooDb, setOdooDb] = React.useState(user.odoo_db ?? "");
  const [odooUserId, setOdooUserId] = React.useState(
    user.odoo_user_id?.toString() ?? "",
  );
  const [saving, setSaving] = React.useState(false);

  const handleSave = async (clear = false): Promise<void> => {
    setSaving(true);
    try {
      const updated = await usersApi.setOdoo(
        user.id,
        clear ? null : odooDb.trim() || null,
        clear
          ? null
          : odooUserId.trim()
            ? Number(odooUserId.trim())
            : null,
      );
      pushToast({
        variant: "success",
        title: clear ? "Đã xóa mapping Odoo" : "Đã lưu mapping Odoo",
      });
      onSaved(updated);
      onClose();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Không lưu được",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <Card
        className="w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <CardContent className="p-5 space-y-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Plug className="size-5" /> Map Odoo identity
          </h2>
          <p className="text-sm text-muted-foreground">
            Gán user <strong>{user.email}</strong> vào Odoo identity. Cần
            cho ERP tool calls (search_partner, create_purchase_order…).
          </p>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Odoo database</label>
            <Input
              value={odooDb}
              onChange={(e) => setOdooDb(e.target.value)}
              placeholder="VD: aiagent"
              disabled={saving}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Odoo user ID</label>
            <Input
              type="number"
              value={odooUserId}
              onChange={(e) => setOdooUserId(e.target.value)}
              placeholder="VD: 2"
              disabled={saving}
            />
          </div>
          <div className="flex justify-between gap-2 pt-2">
            <Button
              variant="ghost"
              onClick={() => void handleSave(true)}
              disabled={saving || (!user.odoo_db && !user.odoo_user_id)}
            >
              Xóa mapping
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} disabled={saving}>
                Hủy
              </Button>
              <Button
                onClick={() => void handleSave(false)}
                disabled={
                  saving ||
                  !odooDb.trim() ||
                  !odooUserId.trim() ||
                  Number.isNaN(Number(odooUserId))
                }
              >
                {saving ? <Loader2 className="size-4 animate-spin mr-1.5" /> : null}
                Lưu
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

interface ResetPasswordDialogProps {
  user: AdminUser;
  onClose: () => void;
}

function ResetPasswordDialog({
  user,
  onClose,
}: ResetPasswordDialogProps): JSX.Element {
  const pushToast = useUIStore((s) => s.pushToast);
  const [pw, setPw] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  const handleSave = async (): Promise<void> => {
    if (pw.length < 6) return;
    setSaving(true);
    try {
      await usersApi.resetPassword(user.id, pw);
      pushToast({
        variant: "success",
        title: "Đã reset mật khẩu",
        description: `User ${user.email} có thể login với password mới`,
      });
      onClose();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Không reset được",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <CardContent className="p-5 space-y-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <KeyRound className="size-5" /> Force reset password
          </h2>
          <p className="text-sm text-muted-foreground">
            Đặt mật khẩu mới cho <strong>{user.email}</strong>. User sẽ login
            được với password này (≥ 6 ký tự).
          </p>
          <Input
            type="text"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="Mật khẩu mới"
            disabled={saving}
            autoFocus
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Hủy
            </Button>
            <Button
              onClick={() => void handleSave()}
              disabled={saving || pw.length < 6}
            >
              {saving ? <Loader2 className="size-4 animate-spin mr-1.5" /> : null}
              Reset
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// Doc 50: Invite Dialog
// ============================================================================

interface InviteDialogProps {
  onClose: () => void;
  onInvited: () => void;
}

function InviteDialog({ onClose, onInvited }: InviteDialogProps): JSX.Element {
  const pushToast = useUIStore((s) => s.pushToast);
  const [email, setEmail] = React.useState("");
  const [displayName, setDisplayName] = React.useState("");
  const [role, setRole] = React.useState<"end_user" | "admin">("end_user");
  const [ttlDays, setTtlDays] = React.useState(7);
  const [submitting, setSubmitting] = React.useState(false);
  const [result, setResult] = React.useState<InviteUserResponse | null>(null);

  const handleInvite = async (): Promise<void> => {
    setSubmitting(true);
    try {
      const r = await usersApi.invite({
        email: email.trim().toLowerCase(),
        display_name: displayName.trim() || undefined,
        roles: [role],
        ttl_days: ttlDays,
      });
      setResult(r);
      onInvited();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Không invite được",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const fullUrl = result
    ? result.setup_url.startsWith("http")
      ? result.setup_url
      : `${window.location.origin}${result.setup_url}`
    : "";

  const handleCopy = async (): Promise<void> => {
    if (!fullUrl) return;
    try {
      await navigator.clipboard.writeText(fullUrl);
      pushToast({
        variant: "success",
        title: "Đã copy URL",
        description: "Paste vào tin nhắn cho user.",
      });
    } catch {
      // ignore
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <Card
        className="w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <CardContent className="p-5 space-y-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <UserPlus className="size-5" /> Invite user
          </h2>
          {result === null ? (
            <>
              <p className="text-sm text-muted-foreground">
                Tạo user mới (chưa password). Hệ thống sinh URL setup, admin
                share với invitee qua tin nhắn / email tự gửi.
              </p>
              <div className="space-y-2">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Email *
                  </label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="user@example.com"
                    disabled={submitting}
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Tên hiển thị
                  </label>
                  <Input
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="VD: Trần Văn B"
                    disabled={submitting}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Role
                    </label>
                    <select
                      className="w-full h-9 rounded-md border bg-background px-3 text-sm"
                      value={role}
                      onChange={(e) =>
                        setRole(e.target.value as "end_user" | "admin")
                      }
                      disabled={submitting}
                    >
                      <option value="end_user">end_user</option>
                      <option value="admin">admin</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Token TTL (ngày)
                    </label>
                    <Input
                      type="number"
                      value={ttlDays}
                      onChange={(e) =>
                        setTtlDays(
                          Math.min(30, Math.max(1, Number(e.target.value) || 7)),
                        )
                      }
                      min={1}
                      max={30}
                      disabled={submitting}
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={onClose}
                  disabled={submitting}
                >
                  Hủy
                </Button>
                <Button
                  onClick={() => void handleInvite()}
                  disabled={submitting || !email.trim()}
                >
                  {submitting ? (
                    <Loader2 className="size-4 animate-spin mr-1.5" />
                  ) : (
                    <Send className="size-4 mr-1.5" />
                  )}
                  Invite
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm">
                <div className="font-medium text-green-900">
                  Invitation đã sinh thành công cho{" "}
                  <span className="font-mono">{result.email}</span>
                </div>
                <div className="text-xs text-green-700 mt-1">
                  Token TTL: {result.expires_in_days} ngày · Single-use
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">
                  Setup URL (share với user qua tin nhắn / email)
                </label>
                <div className="flex gap-2">
                  <Input
                    value={fullUrl}
                    readOnly
                    className="flex-1 font-mono text-xs"
                    onFocus={(e) => e.target.select()}
                  />
                  <Button onClick={() => void handleCopy()}>
                    <Copy className="size-4 mr-1.5" /> Copy
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  User mở URL → set password → tự động login. Token sẽ tự
                  invalid sau khi dùng hoặc hết TTL.
                </p>
              </div>
              <div className="flex justify-end pt-2">
                <Button onClick={onClose}>Đóng</Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function UsersPage(): JSX.Element {
  const pushToast = useUIStore((s) => s.pushToast);
  const [items, setItems] = React.useState<AdminUser[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [savingId, setSavingId] = React.useState<string | null>(null);

  const [odooDialogUser, setOdooDialogUser] = React.useState<AdminUser | null>(
    null,
  );
  const [resetPwDialogUser, setResetPwDialogUser] =
    React.useState<AdminUser | null>(null);
  const [inviteOpen, setInviteOpen] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await usersApi.list(100);
      setItems(r.items);
      setTotal(r.total);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Không tải được",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setLoading(false);
    }
  }, [pushToast]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleToggleActive = async (u: AdminUser): Promise<void> => {
    setSavingId(u.id);
    try {
      const updated = await usersApi.setActive(u.id, !u.is_active);
      setItems((prev) => prev.map((x) => (x.id === u.id ? updated : x)));
      pushToast({
        variant: "success",
        title: updated.is_active ? "Đã kích hoạt" : "Đã khóa",
      });
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Không thay đổi được",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setSavingId(null);
    }
  };

  const handleToggleAdmin = async (u: AdminUser): Promise<void> => {
    setSavingId(u.id);
    try {
      const isAdmin = u.roles.includes("admin");
      const updated = isAdmin
        ? await usersApi.revokeRole(u.id, "admin")
        : await usersApi.grantRole(u.id, "admin");
      setItems((prev) => prev.map((x) => (x.id === u.id ? updated : x)));
      pushToast({
        variant: "success",
        title: isAdmin ? "Đã revoke admin" : "Đã grant admin",
      });
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Không thay đổi được",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setSavingId(null);
    }
  };

  const handleReissueInvite = async (u: AdminUser): Promise<void> => {
    setSavingId(u.id);
    try {
      const r = await usersApi.reissueInvite(u.id, 7);
      const fullUrl = r.setup_url.startsWith("http")
        ? r.setup_url
        : `${window.location.origin}${r.setup_url}`;
      try {
        await navigator.clipboard.writeText(fullUrl);
        pushToast({
          variant: "success",
          title: "Đã sinh URL mới + copy clipboard",
          description: `Share với ${u.email} (TTL ${r.expires_in_days}d).`,
        });
      } catch {
        pushToast({
          variant: "success",
          title: "Đã sinh URL mới",
          description: fullUrl,
        });
      }
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Không sinh được token",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setSavingId(null);
    }
  };

  // Doc 54: admin sinh reset URL cho user đã có native password
  const handleGenerateResetLink = async (u: AdminUser): Promise<void> => {
    setSavingId(u.id);
    try {
      const r = await usersApi.generateResetLink(u.id, 1);
      const fullUrl = r.reset_url.startsWith("http")
        ? r.reset_url
        : `${window.location.origin}${r.reset_url}`;
      try {
        await navigator.clipboard.writeText(fullUrl);
        pushToast({
          variant: "success",
          title: "Reset URL đã copy clipboard",
          description: `Share với ${u.email} (TTL ${r.expires_in_days}d).`,
        });
      } catch {
        pushToast({
          variant: "success",
          title: "Đã sinh reset URL",
          description: fullUrl,
        });
      }
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Không sinh được reset URL",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="container max-w-7xl py-6 space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <UsersIcon className="size-6" /> Users — Admin
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Quản lý user, gán Odoo identity, reset password, role. Doc 47.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {total} users
          </span>
          <Button variant="outline" size="sm" onClick={() => void refresh()}>
            <RefreshCw className="size-4 mr-1.5" /> Reload
          </Button>
          <Button size="sm" onClick={() => setInviteOpen(true)}>
            <UserPlus className="size-4 mr-1.5" /> Invite user
          </Button>
        </div>
      </div>

      {loading ? (
        <Card>
          <CardContent className="p-6 flex items-center gap-2 text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Đang tải…
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center space-y-2">
            <UsersIcon className="size-8 mx-auto text-muted-foreground" />
            <p className="font-medium">Chưa có user nào</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground border-b bg-muted/40">
                <tr>
                  <th className="px-4 py-2">User</th>
                  <th className="px-4 py-2">Roles</th>
                  <th className="px-4 py-2">Odoo identity</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Last login</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((u) => {
                  const isAdmin = u.roles.includes("admin");
                  const busy = savingId === u.id;
                  return (
                    <tr key={u.id} className="border-b last:border-0">
                      <td className="px-4 py-3 align-top">
                        <div className="font-medium">
                          {u.display_name ?? u.email.split("@")[0]}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {u.email}
                        </div>
                        <div className="text-[10px] text-muted-foreground font-mono">
                          {u.id.slice(0, 8)}…
                          {u.has_password ? "" : " · no-pwd"}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="flex flex-wrap gap-1">
                          {u.roles.length === 0 ? (
                            <span className="text-xs text-muted-foreground">—</span>
                          ) : (
                            u.roles.map((r) => (
                              <span
                                key={r}
                                className={
                                  "text-[10px] px-1.5 py-0.5 rounded font-medium " +
                                  (r === "admin"
                                    ? "bg-primary/20 text-primary"
                                    : "bg-muted")
                                }
                              >
                                {r}
                              </span>
                            ))
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        {u.odoo_db && u.odoo_user_id ? (
                          <div className="text-xs">
                            <div className="font-mono">{u.odoo_db}</div>
                            <div className="text-muted-foreground">
                              uid #{u.odoo_user_id}
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">
                            chưa map
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {u.is_active ? (
                          <span className="inline-flex items-center gap-1 text-xs text-green-700">
                            <CheckCircle2 className="size-3" /> Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-red-700">
                            <XCircle className="size-3" /> Locked
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top text-xs text-muted-foreground">
                        {fmtDate(u.last_login_at)}
                      </td>
                      <td className="px-4 py-3 align-top text-right space-x-1 whitespace-nowrap">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy}
                          onClick={() => setOdooDialogUser(u)}
                          title="Map Odoo identity"
                        >
                          <Link2 className="size-3 mr-1" />
                          Odoo
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy}
                          onClick={() => setResetPwDialogUser(u)}
                          title="Force reset password"
                        >
                          <KeyRound className="size-3 mr-1" />
                          Reset
                        </Button>
                        {!u.has_password ? (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busy}
                            onClick={() => void handleReissueInvite(u)}
                            title="Re-issue invitation token + copy URL"
                          >
                            <Send className="size-3 mr-1" />
                            Invite
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busy}
                            onClick={() => void handleGenerateResetLink(u)}
                            title="Doc 54: Generate password reset URL"
                          >
                            <Mail className="size-3 mr-1" />
                            Reset link
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant={isAdmin ? "default" : "outline"}
                          disabled={busy}
                          onClick={() => void handleToggleAdmin(u)}
                          title={isAdmin ? "Revoke admin" : "Grant admin"}
                        >
                          <ShieldAlert className="size-3 mr-1" />
                          {isAdmin ? "Admin" : "+ Admin"}
                        </Button>
                        <Button
                          size="sm"
                          variant={u.is_active ? "outline" : "default"}
                          disabled={busy}
                          onClick={() => void handleToggleActive(u)}
                          title={u.is_active ? "Khóa user" : "Kích hoạt"}
                        >
                          {busy ? (
                            <Loader2 className="size-3 animate-spin" />
                          ) : u.is_active ? (
                            "Lock"
                          ) : (
                            "Unlock"
                          )}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {odooDialogUser ? (
        <OdooMappingDialog
          user={odooDialogUser}
          onClose={() => setOdooDialogUser(null)}
          onSaved={(next) =>
            setItems((prev) => prev.map((x) => (x.id === next.id ? next : x)))
          }
        />
      ) : null}

      {resetPwDialogUser ? (
        <ResetPasswordDialog
          user={resetPwDialogUser}
          onClose={() => setResetPwDialogUser(null)}
        />
      ) : null}

      {inviteOpen ? (
        <InviteDialog
          onClose={() => setInviteOpen(false)}
          onInvited={() => void refresh()}
        />
      ) : null}
    </div>
  );
}
